import { useEffect, useState } from 'react';

import useResponsive from '@/shared/hooks/useResponsive';

import Button from '@/shared/components/button/Button';
import BouncySumsHighScoreConfetti from '@/features/bouncySums/components/BouncySumsHighScoreConfetti';

import pufferfishDefaultDesktopUrl from '@/features/bouncySums/assets/images/pufferfish-default-desktop.svg?url';
import pufferfishDefaultMobileUrl from '@/features/bouncySums/assets/images/pufferfish-default-mobile.svg?url';
import pufferfishDefaultTabletUrl from '@/features/bouncySums/assets/images/pufferfish-default-tablet.svg?url';
import starfishUrl from '@/features/bouncySums/assets/images/starfish.svg?url';

import '@/features/bouncySums/styles/bouncy-sums-end-screen.scss';

import type { BouncySumsLabels } from '@/features/bouncySums/types';

const SCORE_COUNT_UP_DURATION_MS = 1600;

type BouncySumsEndScreenProps = {
	answeredCount: number;
	isHighScore: boolean;
	labels: BouncySumsLabels;
	score: number;
	selectedTables: number[];
	onReplay: () => void;
	onTableSelection: () => void;
};

const BouncySumsEndScreen = ({
	answeredCount,
	isHighScore,
	labels,
	score,
	selectedTables,
	onReplay,
	onTableSelection,
}: BouncySumsEndScreenProps) => {
	const { isDesktop, isTablet } = useResponsive();
	const [animatedScore, setAnimatedScore] = useState(0);
	const pufferfishImageSrc = isDesktop
		? pufferfishDefaultDesktopUrl
		: isTablet
		? pufferfishDefaultTabletUrl
		: pufferfishDefaultMobileUrl;
	const starfishClassName = `bouncy-sums-end-screen__starfish${
		isHighScore ? ' bouncy-sums-end-screen__starfish--high-score' : ''
	}`;

	useEffect(() => {
		const targetScore = Math.max(0, Math.round(score));

		if (targetScore === 0) {
			setAnimatedScore(0);
			return;
		}

		let animationFrameId = 0;
		const startTime = performance.now();

		const updateScore = (currentTime: number) => {
			const progress = Math.min((currentTime - startTime) / SCORE_COUNT_UP_DURATION_MS, 1);
			const easedProgress = 1 - Math.pow(1 - progress, 3);

			setAnimatedScore(Math.round(targetScore * easedProgress));

			if (progress < 1) {
				animationFrameId = window.requestAnimationFrame(updateScore);
			}
		};

		setAnimatedScore(0);
		animationFrameId = window.requestAnimationFrame(updateScore);

		return () => {
			window.cancelAnimationFrame(animationFrameId);
		};
	}, [score]);

	return (
		<div className="bouncy-sums-end-screen">
			<div className="bouncy-sums-end-screen__content">
				<img className="bouncy-sums-end-screen__pufferfish" src={pufferfishImageSrc} alt="" />
				<h2 className="bouncy-sums-end-screen__title">{labels.endTitle}</h2>
				<div className="bouncy-sums-end-screen__score">{animatedScore}</div>
				<div className="bouncy-sums-end-screen__starfish-wrap">
					{isHighScore ? <BouncySumsHighScoreConfetti /> : null}
					<img className={starfishClassName} src={starfishUrl} alt="" />
				</div>
				<dl className="bouncy-sums-end-screen__stats">
					<dt>{labels.tablesLabel}</dt>
					<dd>{selectedTables.join(', ')}</dd>
					<dt>{labels.tablesAnsweredLabel}</dt>
					<dd>{answeredCount}</dd>
				</dl>
				<div className="bouncy-sums-end-screen__actions">
					<Button
						btnType="button"
						className="bouncy-sums-end-screen__button"
						iconName="icon-repeat"
						title={labels.replay}
						type="primary"
						onClick={onReplay}
					/>
					<Button
						btnType="button"
						className="bouncy-sums-end-screen__button"
						title={labels.otherTables}
						type="secondary"
						onClick={onTableSelection}
					/>
				</div>
			</div>
		</div>
	);
};

export default BouncySumsEndScreen;
